import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_DARK, NAV, SOCIALS } from "../../lib/landing";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-[100] px-4 sm:px-8 pt-4"
    >
      <div
        className={`mx-auto max-w-[1400px] flex items-center justify-between rounded-full px-4 sm:px-6 py-3 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-brand-bg/70 backdrop-blur-xl shadow-[0_10px_40px_-20px_rgba(28,25,23,0.35)] border border-brand-line/60"
            : "bg-transparent border border-transparent"
        }`}
      >
        <a
          href="#top"
          data-testid="logo-link"
          className="flex items-center gap-2.5 group"
        >
          <img
            src={LOGO_DARK}
            alt="Safe(H)er"
            className="h-8 w-8 object-contain transition-transform duration-500 group-hover:-rotate-6"
          />
          <span className="font-display text-xl tracking-tight text-brand-text">
            Safe<span className="italic text-brand-primary">(H)</span>er
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className="relative text-sm text-brand-muted hover:text-brand-text transition-colors duration-300 py-1 group"
            >
              {item.label}
              <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-brand-primary transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <a
          href="#scarica"
          data-testid="header-download-btn"
          className="hidden md:inline-flex items-center rounded-full bg-brand-text text-brand-inverse text-sm px-5 py-2.5 hover:bg-brand-primary transition-colors duration-300 active:scale-95"
        >
          Scarica l'app
        </a>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden text-brand-text p-1"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden mx-auto max-w-[1400px] mt-2 rounded-3xl bg-brand-bg/95 backdrop-blur-xl border border-brand-line p-6 flex flex-col gap-4"
          >
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className="font-display text-2xl text-brand-text"
              >
                {item.label}
              </a>
            ))}
            <a
              href="#scarica"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex justify-center rounded-full bg-brand-text text-brand-inverse px-5 py-3"
            >
              Scarica l'app
            </a>
            <div className="flex gap-4 pt-2 text-sm text-brand-muted">
              <a href={SOCIALS.instagram} target="_blank" rel="noreferrer">
                Instagram
              </a>
              <a href={SOCIALS.x} target="_blank" rel="noreferrer">
                X
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
